import { Chunk } from "../feature/Chunks/Types/Chunk";
import { Slot } from "../feature/Chunks/Types/Slot";
import { SlotType } from "../feature/Chunks/Enums/SlotType";
/**
 * Replace all slot placeholders in the template string with their corresponding values.
 * A placeholder is denoted with curly braces e.g. {property}.
 *
 * @param template The string that contains placeholders.
 * @param slots    A collection of slots with values to substitute.
 * @returns        The template with all placeholders substituted.
 */
export function fillTemplate(template: string, slots: Slot[]): string {
  let output = template;

  // Slot names that should NOT be quoted in Cypher, as they represent
  // identifiers (like property names) or operators.
  const nonLiteralNames = ["property", "condition", "stat", "operator"];

  // Enum values arrive as strings ("2025"); numeric fields must compile to
  // numbers or they never match the integers stored in Neo4j.
  const isNumericField = slots.some(
    (s) =>
      s.SlotValueTypes?.includes(SlotType.Filter) && s.FieldKind === "numeric",
  );
  const isNumericString = (v: unknown) =>
    typeof v === "string" && v.trim() !== "" && !isNaN(Number(v));

  slots.forEach(({ Name, Value }) => {
    const pattern = new RegExp(`\\{${Name}(?:\\.[a-zA-Z0-9_]+)?\\}`, "g");

    // Array value (multi-select for an `in` filter) -> Cypher list literal,
    // e.g. ["KC", "SF"]. Elements are quoted unless the slot is an identifier
    // or a number on a numeric field.
    if (Array.isArray(Value)) {
      const quoteEl = !nonLiteralNames.includes(Name);
      const items = Value.map((v) =>
        isNumericField && isNumericString(v)
          ? String(Number(v))
          : typeof v === "string" && quoteEl
            ? `"${v}"`
            : String(v),
      );
      output = output.replace(pattern, `[${items.join(", ")}]`);
      return;
    }

    // Convert string back to number for numeric filter values
    // "value" represents stat values (rushing yards, completions, etc.)
    // but we also need to handle other numeric slots like seasons that users type
    let finalValue = Value;
    const numericSlots = ["value", "season", "seasonStart", "seasonEnd"];
    if (numericSlots.includes(Name) && typeof Value === "string" && !isNaN(Number(Value)) && Value.trim() !== "") {
      finalValue = Number(Value);
    }

    // Only quote the value if it's a string AND its name indicates
    // it's a literal value, not an identifier or operator.
    const shouldQuote =
      typeof finalValue === "string" && !nonLiteralNames.includes(Name);
    const replacement = shouldQuote ? `"${finalValue}"` : String(finalValue);

    output = output.replace(pattern, replacement);
  });

  return output;
}

/**
 * Utility that returns a shallow copy of a chunk with English & Cypher strings
 * having their placeholders replaced with current Slot values.
 *
 * Note: the original chunk object is left unmodified to avoid accidental
 * mutation of the static chunk catalogue.
 */

export function buildFilledChunk(original: Chunk): Chunk {
  // Fill the templates with current slot values
  const filledEnglish = fillTemplate(original.EnglishTemplate || original.English, original.Slots);
  const filledCypher = fillTemplate(original.CypherTemplate || original.Cypher, original.Slots);

  return {
    ...original,
    // These are the filled versions used for display and query execution
    English: filledEnglish,
    Cypher: filledCypher,
    // Templates are already provided by the chunk data, just pass them through
    EnglishTemplate: original.EnglishTemplate,
    CypherTemplate: original.CypherTemplate,
  };
}
